import { api } from 'lib/httpClient';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNotification } from 'hooks/ui/useNotification';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import { ApiResponse, SuccessResponse } from 'further-types/api';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';

async function fetchAllTags({ queryKey }) {
  const [, filters] = queryKey;
  const response = await api.get('/tag', {
    params: {
      ...filters,
    },
  });
  return response.data.data;
}

function useDeleteTag() {
  const notification = useNotification();
  const handleApiException = useApiExceptionHandler();
  const queryClient = useQueryClient();

  return useMutation({
    throwOnError: false,
    mutationFn: async (tagId: string) => {
      const result = await api.delete<ApiResponse<SuccessResponse>>(
        `tag/${tagId}`,
      );
      return result.data;
    },
    onSuccess: async (data) => {
      notification.success(data.responseMsg ?? '');
      await queryClient.invalidateQueries({
        queryKey: ['tag'],
      });
    },
    onError: (error: Error) => {
      handleApiException(error);
    },
  });
}

export function useTags({ name }: { name?: string } = {}) {
  const tags = useQuery({
    queryKey: [
      'tag',
      {
        name,
      },
    ],
    queryFn: fetchAllTags,
    placeholderData: keepPreviousData,
    staleTime: QUERY_STALE_TIME_MS,
  });

  return {
    tags,
    deleteTag: useDeleteTag(),
  };
}
